import { DependencyHelper } from '../helpers/DependencyHelper';
import { extractPropertyPaths } from './extractPropertyPaths';

export type DependencyHandler<T> = {
  path: string;
  value: unknown;
  dependencies: BuildDependencyGraphItem<T>;
  key: string;
  mainKey: string;
};

export type DependsOnType = {
  id: string;
  p: string[];
};

export type BuildDependencyGraphItem<T> = {
  /**
   * output contains the value of handlers that are passed while building the dependency graph
   */
  output?: T;
  /**
   * A list of entity IDs that this entity depends on.
   * This is useful for tracking inter-entity dependencies.
   */
  dependsOn?: DependsOnType[];
};

function removeDuplicateDependencyPaths<T>(dependencies: BuildDependencyGraphItem<T>): void {
  if (!dependencies.dependsOn) return;

  dependencies.dependsOn = dependencies.dependsOn.map((dependsOn) => {
    return DependencyHelper.makePropertiesUnique(dependsOn);
  });
}

export type BuildDependencyGraphParams<T> = {
  objects: Record<string, unknown>;
  isValidEntity: (entityId: string) => boolean;
  handlers?: ((props: DependencyHandler<T>) => void)[];
  dedupeHandlers?: ((params: T) => T)[];
};

function processObject<T>({
  isValidEntity,
  handlers,
  mainKey,
  dependencies,
  obj,
  parentPath,
}: {
  obj: unknown;
  parentPath: string;
  mainKey: string;
  dependencies: BuildDependencyGraphItem<T>;
  isValidEntity: (entityId: string) => boolean;
  handlers: ((props: DependencyHandler<T>) => void)[];
}): void {
  // Handle cycles and invalid objects
  if (!obj || typeof obj !== 'object') return;

  if (Array.isArray(obj)) {
    let index = 0;
    for (const item of obj) {
      const currentPath = `${parentPath}[${index++}]`;
      processValue({
        value: item,
        isValidEntity,
        key: '',
        mainKey,
        handlers,
        dependencies,
        path: currentPath,
      });
      processObject({
        obj: item,
        parentPath: currentPath,
        mainKey,
        dependencies,
        isValidEntity,
        handlers,
      });
    }
    return;
  }

  if (obj instanceof Date) return;

  for (const [key, value] of Object.entries(obj)) {
    const currentPath = parentPath ? `${parentPath}.${key}` : key;
    processValue({
      value,
      isValidEntity,
      key,
      mainKey,
      dependencies,
      path: currentPath,
      handlers,
    });
    processObject({
      obj: value,
      parentPath: currentPath,
      mainKey,
      dependencies,
      isValidEntity,
      handlers,
    });
  }
}

function processValue<T>({
  dependencies,
  mainKey,
  key,
  isValidEntity,
  value,
  path,
  handlers,
}: {
  value: unknown;
  path: string;
  key: string;
  mainKey: string;
  dependencies: BuildDependencyGraphItem<T>;
  isValidEntity: (entityId: string) => boolean;
  handlers: ((props: DependencyHandler<T>) => void)[];
}): void {
  for (const handler of handlers) {
    handler({ path, value, dependencies, key, mainKey });
  }

  if (typeof value !== 'string') return;

  const { result } = extractPropertyPaths(value, isValidEntity);

  const entries = Object.entries(result);
  if (entries.length === 0) return;

  if (!dependencies.dependsOn) {
    dependencies.dependsOn = [];
  }

  const dependsOnMap = new Map<string, { index: number; dep: DependsOnType }>();

  for (const [index, dep] of dependencies.dependsOn.entries()) {
    dependsOnMap.set(dep.id, { index, dep });
  }

  for (const [entityId, paths] of entries) {
    if (entityId === mainKey || !paths?.size) continue;
    const existing = dependsOnMap.get(entityId);

    if (existing) {
      const updatedDependsOn = DependencyHelper.updateDependsOn(existing.dep, paths);
      dependencies.dependsOn[existing.index] = updatedDependsOn;
      dependsOnMap.set(entityId, { index: existing.index, dep: updatedDependsOn });
    } else {
      const newDependsOn = DependencyHelper.createDependsOn(entityId, Array.from(paths));
      const newIndex = dependencies.dependsOn.length;
      dependencies.dependsOn.push(newDependsOn);
      dependsOnMap.set(entityId, { index: newIndex, dep: newDependsOn });
    }
  }
}

export function buildDependencyGraph<T>({
  dedupeHandlers,
  handlers = [],
  objects,
  isValidEntity,
}: BuildDependencyGraphParams<T>): Record<string, BuildDependencyGraphItem<T> | undefined> {
  const dependencyGraph: Record<string, BuildDependencyGraphItem<T> | undefined> = {};

  for (const [key, value] of Object.entries(objects)) {
    const dependencies: BuildDependencyGraphItem<T> = { dependsOn: [] };

    // Explicitly process primitive values since processObject only handles objects.
    // Calling processObject afterward is safe as it no-ops for non-objects.
    if (!value || typeof value !== 'object') {
      processValue({
        value,
        isValidEntity,
        key: '',
        mainKey: key,
        dependencies,
        path: '',
        handlers,
      });
    }

    processObject({
      obj: value,
      parentPath: '',
      mainKey: key,
      dependencies,
      isValidEntity,
      handlers,
    });

    dependencyGraph[key] = dependencies;
  }

  for (const key in dependencyGraph) {
    if (!Object.hasOwn(dependencyGraph, key)) continue;
    const dependencies = dependencyGraph[key];
    if (!dependencies) continue;

    removeDuplicateDependencyPaths(dependencies);

    if (dependencies.output && dedupeHandlers && dedupeHandlers.length > 0) {
      let output: T = dependencies.output;
      for (const dedupeHandler of dedupeHandlers) {
        output = dedupeHandler(output);
      }
      dependencies.output = output;
    }
  }

  return dependencyGraph;
}
